import DOMPurify from 'dompurify'
import type { ReactNode } from 'react'
import { looksLikeHtml } from '@/lib/richText'
import { cn } from '@/lib/utils'

/**
 * Read-only rendering of a rich-text body (note, external input).
 *
 * The whitelist mirrors `backend/app/preinvest/rich_text.py` exactly. The
 * backend already sanitised on write, so this is the second of two layers:
 * rows written before spec-58, or by anything that bypassed the API, still
 * cannot put a script in the DOM.
 *
 * Bodies that do not start with a tag are plain text from before the one-off
 * conversion. They are rendered as text, not as HTML, so a `<` in "omzet < 1M"
 * stays visible instead of swallowing the rest of the line.
 */

const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'h2', 'h3', 'mark', 'a']
const ALLOWED_ATTR = ['href', 'rel', 'target']

const URL_RE = /\bhttps?:\/\/[^\s<>"']+/g
const TRAILING = /[.,;:!?)\]]+$/
const LINK_CLASS = 'text-primary underline underline-offset-2 hover:no-underline'

/** Split a text into plain parts and web addresses; trailing punctuation stays outside the link. */
function splitUrls(text: string): { text: string; url: boolean }[] {
  const parts: { text: string; url: boolean }[] = []
  let last = 0
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0
    const url = m[0].slice(0, m[0].length - (TRAILING.exec(m[0])?.[0].length ?? 0))
    if (url.length <= 'https://'.length - 1) continue
    if (start > last) parts.push({ text: text.slice(last, start), url: false })
    parts.push({ text: url, url: true })
    last = start + url.length
  }
  if (last < text.length) parts.push({ text: text.slice(last), url: false })
  return parts
}

function linkifyText(text: string): ReactNode[] {
  return splitUrls(text).map((part, i) =>
    part.url ? (
      <a key={i} href={part.text} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
        {part.text}
      </a>
    ) : (
      part.text
    ),
  )
}

/** Sanitise, then make every web address in a text node (outside an `<a>`) a link. */
function linkifyHtml(html: string): string {
  const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR })
  const doc = new DOMParser().parseFromString(`<body>${clean}</body>`, 'text/html')
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!(n.parentElement?.closest('a'))) nodes.push(n as Text)
  }
  for (const node of nodes) {
    const parts = splitUrls(node.data)
    if (!parts.some((p) => p.url)) continue
    const frag = doc.createDocumentFragment()
    for (const part of parts) {
      if (!part.url) {
        frag.append(part.text)
        continue
      }
      const a = doc.createElement('a')
      a.setAttribute('href', part.text)
      a.textContent = part.text
      frag.append(a)
    }
    node.replaceWith(frag)
  }
  for (const a of Array.from(doc.body.querySelectorAll('a'))) {
    a.setAttribute('target', '_blank')
    a.setAttribute('rel', 'noopener noreferrer')
    a.setAttribute('class', LINK_CLASS)
  }
  return doc.body.innerHTML
}

export function RichTextView({ html, className }: { html: string; className?: string }) {
  if (!looksLikeHtml(html)) {
    return (
      <p className={cn('max-w-[72ch] text-sm whitespace-pre-wrap', className)}>{linkifyText(html)}</p>
    )
  }
  return (
    <div
      className={cn('vonk-prose max-w-[72ch] text-sm leading-[1.55]', className)}
      // Sanitized: the whitelist matches the backend's, so nothing that a later
      // edit would strip can reach the DOM here either.
      dangerouslySetInnerHTML={{ __html: linkifyHtml(html) }}
    />
  )
}
